import { useEffect, useState, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSiteInfo } from "@/hooks/useSiteInfo";
import type { NavLinkItem, NavSubLink } from "@/lib/types";
import LanguageSwitcher from "./LanguageSwitcher";

interface NavbarProps {
  variant?: "overlay" | "solid";
}

export default function Navbar({ variant = "overlay" }: NavbarProps) {
  const { t } = useTranslation();
  const site = useSiteInfo();
  const navLinksRaw = t("common.nav.links", { returnObjects: true }) as unknown as NavLinkItem[];
  const navLinks = Array.isArray(navLinksRaw) ? navLinksRaw : [];

  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const location = useLocation();
  const dropdownTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setOpenDropdown(null);
  }, [location.pathname]);

  const isSolid = variant === "solid" || scrolled;

  const handleDropdownEnter = (label: string) => {
    if (dropdownTimer.current) clearTimeout(dropdownTimer.current);
    setOpenDropdown(label);
  };

  const handleDropdownLeave = () => {
    dropdownTimer.current = setTimeout(() => setOpenDropdown(null), 200);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isSolid
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/70"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
        {/* 왼쪽 그룹: 로고 + 언어 토글 */}
        <div className="flex items-center gap-4">
          <Link to="/" className="flex items-center gap-3 cursor-pointer">
            <span
              className={`h-[46px] w-[80px] rounded-md flex items-center justify-center overflow-hidden ${
                isSolid ? "bg-background-50" : "bg-background-50/15 backdrop-blur"
              }`}
            >
              <img
                src="https://storage.readdy-site.link/project_files/e8666665-2242-48cf-9593-4ebcd3ad786e/6437e91a-c2b1-4345-9a35-15b2da6f9f43_compressed_logo.webp"
                alt={site.brandKo}
                className="h-full w-full object-contain"
                style={{ transform: "scaleY(1.18)" }}
              />
            </span>
            <span
              className={`flex flex-col leading-tight px-1 ${
                isSolid ? "text-foreground-950" : "text-background-50"
              }`}
            >
              <span className="font-heading font-bold text-[15px] tracking-tight whitespace-nowrap">
                {site.brandShort}
              </span>
              <span
                className={`text-[10px] tracking-[0.18em] uppercase ${
                  isSolid ? "text-foreground-500" : "text-background-50/70"
                }`}
              >
                HANKUK RUBBER TECH
              </span>
            </span>
          </Link>
          <div className="hidden lg:flex items-center">
            <LanguageSwitcher variant={isSolid ? "light" : "dark"} />
          </div>
        </div>

        <div className="hidden lg:flex items-center gap-1">
          {navLinks.map((l: NavLinkItem) => {
            const hasSub = l.sub && l.sub.length > 0;
            const isActive =
              l.to === "/" ? location.pathname === "/" : location.pathname.startsWith(l.to);

            if (hasSub) {
              return (
                <div
                  key={l.label}
                  className="relative"
                  onMouseEnter={() => handleDropdownEnter(l.label)}
                  onMouseLeave={handleDropdownLeave}
                >
                  <NavLink
                    to={l.to}
                    className={`relative px-4 py-2 text-sm font-medium tracking-wide rounded-md transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1 ${
                      isSolid
                        ? isActive
                          ? "text-primary-600"
                          : "text-foreground-700 hover:text-foreground-950"
                        : isActive
                          ? "text-background-50"
                          : "text-background-50/80 hover:text-background-50"
                    }`}
                  >
                    {l.label}
                    <span className="w-3 h-3 flex items-center justify-center">
                      <i className={`ri-arrow-down-s-line text-[10px] transition-transform ${openDropdown === l.label ? "rotate-180" : ""}`} />
                    </span>
                  </NavLink>
                  {openDropdown === l.label && (
                    <div className="absolute top-full left-0 mt-1 w-48 py-2 rounded-lg bg-background-50 border border-background-200/70 shadow-lg">
                      {l.sub!.map((sub: NavSubLink) => (
                        <NavLink
                          key={sub.to}
                          to={sub.to}
                          className={({ isActive: subActive }) =>
                            `block px-4 py-2.5 text-sm transition-colors cursor-pointer whitespace-nowrap ${
                              subActive
                                ? "text-primary-600 bg-primary-50 font-medium"
                                : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
                            }`
                          }
                        >
                          {sub.label}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive: linkActive }) =>
                  `relative px-4 py-2 text-sm font-medium tracking-wide rounded-md transition-colors cursor-pointer whitespace-nowrap ${
                    isSolid
                      ? linkActive
                        ? "text-primary-600"
                        : "text-foreground-700 hover:text-foreground-950"
                      : linkActive
                        ? "text-background-50"
                        : "text-background-50/80 hover:text-background-50"
                  }`
                }
              >
                {l.label}
              </NavLink>
            );
          })}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={site.telLink}
            className={`inline-flex items-center gap-2 text-sm font-medium cursor-pointer whitespace-nowrap ${
              isSolid ? "text-foreground-700 hover:text-primary-600" : "text-background-50/90 hover:text-background-50"
            }`}
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-phone-line text-[14px]" />
            </span>
            {site.tel}
          </a>
          <Link
            to="/quotation"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
          >
            {t("common.nav.quoteRequest")}
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line" />
            </span>
          </Link>
        </div>

        <button
          type="button"
          aria-label={t("common.nav.menuOpen")}
          onClick={() => setOpen((v) => !v)}
          className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-md cursor-pointer ${
            isSolid ? "text-foreground-950" : "text-background-50"
          }`}
        >
          <i className={`text-xl ${open ? "ri-close-line" : "ri-menu-line"}`} />
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-background-200/70 bg-background-50 overflow-y-auto max-h-[calc(100vh-72px)]">
          <div className="px-6 py-4 flex flex-col gap-1">
            <div className="flex items-center justify-end py-2">
              <LanguageSwitcher variant="light" />
            </div>
            {navLinks.map((l: NavLinkItem) => {
              const hasSub = l.sub && l.sub.length > 0;
              const isActive =
                l.to === "/" ? location.pathname === "/" : location.pathname.startsWith(l.to);
              if (hasSub) {
                return (
                  <div key={l.label}>
                    <button
                      type="button"
                      onClick={() => setOpenDropdown(openDropdown === l.label ? null : l.label)}
                      className={`w-full px-3 py-3 rounded-md text-sm font-medium text-left flex items-center justify-between cursor-pointer ${
                        isActive ? "text-primary-600 bg-primary-50" : "text-foreground-800 hover:bg-background-100"
                      }`}
                    >
                      {l.label}
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className={`ri-arrow-down-s-line transition-transform ${openDropdown === l.label ? "rotate-180" : ""}`} />
                      </span>
                    </button>
                    {openDropdown === l.label && (
                      <div className="ml-3 pl-3 border-l-2 border-background-200">
                        {l.sub!.map((sub: NavSubLink) => (
                          <NavLink
                            key={sub.to}
                            to={sub.to}
                            className={({ isActive: subActive }) =>
                              `block px-3 py-2.5 rounded-md text-sm cursor-pointer ${
                                subActive
                                  ? "text-primary-600 font-medium"
                                  : "text-foreground-600 hover:text-foreground-950"
                              }`
                            }
                          >
                            {sub.label}
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  className={({ isActive: linkActive }) =>
                    `px-3 py-3 rounded-md text-sm font-medium cursor-pointer ${
                      linkActive
                        ? "bg-primary-50 text-primary-700"
                        : "text-foreground-800 hover:bg-background-100"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              );
            })}
            <div className="mt-3 flex flex-col gap-2 pt-3 border-t border-background-200">
              <a href={site.telLink} className="px-3 py-2 text-sm text-foreground-700 cursor-pointer">
                <i className="ri-phone-line mr-2" />
                {site.tel}
              </a>
              <a href={site.emailLink} className="px-3 py-2 text-sm text-foreground-700 cursor-pointer">
                <i className="ri-mail-line mr-2" />
                {site.email}
              </a>
              <Link
                to="/contact"
                className="mt-1 inline-flex items-center justify-center px-5 py-3 rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 text-sm font-semibold cursor-pointer whitespace-nowrap"
              >
                {t("common.nav.quoteRequestFull")}
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}